// Updates: news from the academy for everyone or for one batch. Teachers post to their batches,
// admins to anyone. Students with reminders on also get a notification.
import { Fragment } from 'preact';
import { useEffect, useLayoutEffect, useState } from 'preact/hooks';
import { useRoute } from '../router.js';
import { rpc } from '../api.js';
import { useApp } from '../state.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Field, Loading, Sheet, useRpc, useToast } from '../ui/components.jsx';
import { dateTime } from '../format.js';

const URL_RE = /(https:\/\/[^\s<>"']+)/g;

// Text with its https links made clickable (nothing else is turned into HTML).
function Linked({ text }) {
  const out = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    let url = m[0];
    const trail = url.match(/[.,;:!?)\]]+$/);
    if (trail) url = url.slice(0, -trail[0].length);
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<a href={url} target="_blank" rel="noopener noreferrer" key={m.index}>{url}</a>);
    last = m.index + url.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Body({ text }) {
  const paras = String(text || '').trim().split(/\n\s*\n/).filter(Boolean);
  return paras.map((p, i) => (
    <p key={i}>{p.split('\n').map((line, j) => <Fragment key={j}>{j ? <br /> : null}<Linked text={line} /></Fragment>)}</p>
  ));
}

function UpdateCard({ u, highlight, onDelete }) {
  return (
    <article id={`u-${u.id}`} class={`update${u.read ? '' : ' is-new'}${highlight ? ' is-highlight' : ''}`} aria-labelledby={`ut-${u.id}`}>
      <div class="update-head">
        <h2 id={`ut-${u.id}`}>{u.pinned ? <Icon name="pin" label="Pinned" /> : null}<span>{u.title}</span></h2>
        {u.read ? null : <Chip tone="gold">New</Chip>}
      </div>
      <p class="update-meta">
        <span class={`audience${u.batch ? '' : ' is-all'}`}>{u.batch ? u.batch.name : 'Everyone'}</span>
        <span>{dateTime(u.created_at)}</span>
        <span>{u.author}</span>
      </p>
      {u.body ? <div class="update-body"><Body text={u.body} /></div> : null}
      {u.can_delete ? (
        <div class="update-actions">
          <Button variant="ghost" size="sm" icon="trash" onClick={() => onDelete(u)} aria-label={`Delete “${u.title}”`}>Delete</Button>
        </div>
      ) : null}
    </article>
  );
}

export function Updates() {
  const { reloadHome } = useApp();
  const { query } = useRoute();
  const toast = useToast();
  const highlight = query.get('u');
  const { data, error, loading, reload } = useRpc('my_updates');
  const [posting, setPosting] = useState(false);

  // Seen now: mark them read (they keep their "New" label until the next visit).
  useEffect(() => {
    if (!data) return;
    const unread = data.updates.filter((u) => !u.read).map((u) => u.id);
    if (unread.length) rpc('mark_updates_read', { p_ids: unread }).then(() => reloadHome(true)).catch(() => {});
    if (highlight) {
      const el = document.getElementById(`u-${highlight}`);
      if (el) el.scrollIntoView({ block: 'start' });
    }
  }, [data]);

  const remove = async (u) => {
    if (!confirm(`Delete “${u.title}”? Students won’t see it any more.`)) return;
    try {
      await rpc('staff_delete_update', { p_id: u.id });
      toast('Update deleted');
      reload(true);
    } catch (e) { toast(e.message, 'bad'); }
  };

  return (
    <Shell title="Updates" actions={data && data.can_post ? <Button variant="gold" size="sm" icon="megaphone" onClick={() => setPosting(true)}>Post an update</Button> : null}>
      {loading && !data ? <Loading rows={3} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !data.updates.length ? (
        <Empty icon="megaphone" title="No updates yet"
          action={data.can_post ? <Button variant="navy" icon="megaphone" onClick={() => setPosting(true)}>Post an update</Button> : null}>
          News from the academy, such as holidays, mock exams and timetable changes, shows up here.
        </Empty>
      ) : null}
      {data && data.updates.length ? (
        <div class="updates">{data.updates.map((u) => <UpdateCard key={u.id} u={u} highlight={u.id === highlight} onDelete={remove} />)}</div>
      ) : null}
      {data && data.can_post ? <PostUpdate open={posting} data={data} onClose={() => setPosting(false)} onSaved={() => reload(true)} /> : null}
    </Shell>
  );
}

function PostUpdate({ open, data, onClose, onSaved }) {
  const toast = useToast();
  const first = data.can_post_everyone ? '' : (data.audiences[0] ? data.audiences[0].id : '');
  const [f, setF] = useState({ batch: first, title: '', body: '', pinned: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useLayoutEffect(() => { if (open) { setErr(null); setF({ batch: first, title: '', body: '', pinned: false }); } }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await rpc('staff_post_update', { p_batch_id: f.batch || null, p_title: f.title, p_body: f.body, p_pinned: f.pinned });
      toast('Posted. Students see it in Updates.');
      onSaved();
      onClose();
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  return (
    <Sheet title="Post an update" open={open} onClose={onClose}>
      <form class="form" onSubmit={save}>
        <Field label="Who it’s for">{(id) => (
          <select id={id} value={f.batch} onChange={set('batch')}>
            {data.can_post_everyone ? <option value="">Everyone</option> : null}
            {data.audiences.map((b) => <option value={b.id} key={b.id}>{b.name}</option>)}
          </select>
        )}</Field>
        <Field label="Title">{(id) => <input id={id} type="text" maxLength={120} required value={f.title} onInput={set('title')} placeholder="Mock exam on Sunday" />}</Field>
        <Field label="Message" hint={`${f.body.length} of 4,000 characters. Links starting with https:// become clickable.`}>{(id) => (
          <textarea id={id} rows={6} maxLength={4000} value={f.body} onInput={set('body')} />
        )}</Field>
        <label class="check"><input type="checkbox" checked={f.pinned} onChange={set('pinned')} /><span>Keep at the top</span></label>
        <p class="fine"><Icon name="bell" />Students with reminders on get a notification within 5 minutes.</p>
        {err ? <p class="field-error" role="alert">{err}</p> : null}
        <div class="form-actions">
          <Button type="submit" variant="navy" icon="megaphone" busy={busy}>Post</Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Sheet>
  );
}
